import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { ChevronDown, Globe } from 'lucide-react';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../hooks/useTranslation';
import EditableText from './EditableText';
import { swapLangInPath, withLang, LANG_CODES } from './LanguageRouter';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { currentLanguage, changeLanguage } = useLanguage();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const isHomePath = () => {
    const segments = location.pathname.split('/').filter(Boolean);
    // Home is "/<lang>" with no further segments
    return segments.length === 1 && LANG_CODES.includes(segments[0]);
  };

  const goToHomeSection = (sectionId) => {
    if (!isHomePath()) {
      navigate(withLang(currentLanguage));
      setTimeout(() => {
        if (sectionId) {
          document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    } else if (sectionId) {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleLanguageChange = (newLang) => {
    changeLanguage(newLang);
    const newPath = swapLangInPath(location.pathname, newLang);
    navigate(newPath + location.search + location.hash);
  };

  const languages = [
    { code: 'en', name: t('languages.en'), flag: '🇺🇸' },
    { code: 'es', name: t('languages.es'), flag: '🇪🇸' },
    { code: 'de', name: t('languages.de'), flag: '🇩🇪' },
    { code: 'sl', name: t('languages.sl'), flag: '🇸🇮' },
    { code: 'sr', name: t('languages.sr'), flag: '🇷🇸' },
  ];

  const currentLang = languages.find(lang => lang.code === currentLanguage) || languages[0];

  const navItems = [
    { label: t('nav.home'), href: '#', action: () => goToHomeSection(null) },
    { label: t('nav.company'), href: '#about', action: () => goToHomeSection('about') },
    { label: t('nav.sourcing'), href: '#services', action: () => goToHomeSection('services') },
    { label: t('nav.projects'), href: '#projects', action: () => goToHomeSection('projects') },
    { label: t('nav.blog'), href: '#blog', action: () => goToHomeSection('blog') },
    { label: t('nav.partners'), href: `/${currentLanguage}/partners`, action: () => navigate(withLang(currentLanguage, '/partners')) },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200/80">
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        <div className="flex items-center justify-between py-3">
          {/* Logo - Clickable, returns to homepage */}
          <button 
            onClick={() => goToHomeSection(null)}
            className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0"
            data-testid="header-logo"
          >
            {/* Three vertical stripes with blue gradient */}
            <div className="flex items-end space-x-0.5 sm:space-x-1">
              <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-t from-blue-600 to-blue-400 rounded-full shadow-sm"></div>
              <div className="w-1.5 sm:w-2 h-8 sm:h-10 bg-gradient-to-t from-blue-700 to-blue-500 rounded-full shadow-sm"></div>
              <div className="w-1.5 sm:w-2 h-5 sm:h-7 bg-gradient-to-t from-blue-600 to-blue-400 rounded-full shadow-sm"></div>
            </div>
            {/* Company name on ONE LINE */}
            <span className="text-slate-900 font-bold tracking-wide text-base sm:text-lg md:text-xl lg:text-2xl whitespace-nowrap">
              TOWER <span className="text-blue-600">TECHNOLOGY</span>
            </span>
          </button>

          {/* Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navItems.map((item, index) => (
              <button
                key={index}
                onClick={item.action}
                className="text-slate-700 hover:text-blue-600 transition-colors duration-300 text-sm font-medium tracking-wide whitespace-nowrap"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Right side buttons */}
          <div className="flex items-center space-x-4">
          {/* Contact Button & Language Dropdown - Desktop */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button 
              onClick={() => goToHomeSection('contact')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 text-sm font-medium transition-all duration-300 transform hover:scale-105 shadow-sm"
              size="sm"
              data-testid="header-contact-button"
            >
              {t('nav.contact')}
            </Button>
            
            {/* Language Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center space-x-2 text-slate-700 hover:text-blue-600 transition-colors duration-300 px-3 py-2 rounded-lg border border-slate-200 hover:border-blue-400" data-testid="language-switcher-toggle">
                  <Globe className="w-4 h-4" />
                  <span className="text-sm font-medium">{currentLang.flag}</span>
                  <span className="text-sm hidden sm:block">{currentLang.name}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                className="w-56 bg-white border border-slate-200 shadow-xl rounded-lg mt-2 max-h-80 overflow-y-auto z-[9999]"
                sideOffset={5}
              >
                {languages.map((language) => (
                  <DropdownMenuItem
                    key={language.code}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleLanguageChange(language.code);
                    }}
                    className={`flex items-center space-x-3 px-4 py-3 text-sm hover:bg-slate-50 cursor-pointer transition-colors duration-200 pointer-events-auto ${
                      currentLanguage === language.code ? 'bg-blue-50 text-blue-700' : 'text-slate-700'
                    }`}
                    data-testid={`language-option-${language.code}`}
                  >
                    <span className="text-lg">{language.flag}</span>
                    <span className="font-medium">{language.name}</span>
                    {currentLanguage === language.code && (
                      <span className="ml-auto text-blue-600 text-xs">✓</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile menu button & language */}
          <div className="lg:hidden flex items-center space-x-2">
            {/* Mobile Language Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center space-x-1 text-slate-700 hover:text-blue-600 transition-colors duration-300 px-2 py-2 rounded-lg border border-slate-200 hover:border-blue-400">
                  <Globe className="w-4 h-4" />
                  <span className="text-sm font-medium">{currentLang.flag}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                className="w-48 bg-white border border-slate-200 shadow-xl rounded-lg mt-2 max-h-80 overflow-y-auto z-[9999]"
                sideOffset={5}
              >
                {languages.map((language) => (
                  <DropdownMenuItem
                    key={language.code}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleLanguageChange(language.code);
                    }}
                    className={`flex items-center space-x-3 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer transition-colors duration-200 pointer-events-auto ${
                      currentLanguage === language.code ? 'bg-blue-50 text-blue-700' : 'text-slate-700'
                    }`}
                  >
                    <span className="text-base">{language.flag}</span>
                    <span className="font-medium text-xs">{language.name}</span>
                    {currentLanguage === language.code && (
                      <span className="ml-auto text-blue-600 text-xs">✓</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

          {/* Mobile menu button */}
          <button
            className="lg:hidden text-slate-700 p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-button"
          >
            <div className="space-y-1.5">
              <div className="w-5 h-0.5 bg-slate-700"></div>
              <div className="w-5 h-0.5 bg-slate-700"></div>
              <div className="w-5 h-0.5 bg-slate-700"></div>
            </div>
          </button>
          </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="lg:hidden mt-2 bg-white border border-slate-200 rounded-lg p-4 shadow-lg">
            {navItems.map((item, index) => (
              <button
                key={index}
                onClick={() => {
                  item.action();
                  setIsMenuOpen(false);
                }}
                className="block text-slate-700 hover:text-blue-600 py-2 text-sm font-medium w-full text-left"
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;